import React, { useEffect, useReducer, useState } from 'react';
import { callPipedrive } from '../../../pipedrive';
import { Alert, Spin, Empty } from 'antd';
import reducer, { initialState } from '../pipedriveDealsReducer';
import { KanbanBoard } from './Kanban/kanban-board';
import type { Stage, Deal as KanbanDeal } from './lib/types'; // Ensure this path is correct

// Helper function to process the Pipedrive API response
const processPipedriveResponse = (pipedriveData: any): Stage[] => {
  if (
    !pipedriveData ||
    !pipedriveData.data ||
    !pipedriveData.related_objects ||
    !pipedriveData.related_objects.stage
  ) {
    console.error('Invalid Pipedrive data structure:', pipedriveData);
    return [];
  }

  const rawDeals = pipedriveData.data;
  const pdStages = pipedriveData.related_objects.stage;

  // Create a map of stages for easy lookup and ordering
  // Temporarily hold pdStage for sorting, then map to Stage type
  const tempStagesArray: any[] = Object.values(pdStages);

  // Sort stages by Pipedrive's order_nr
  tempStagesArray.sort((a: any, b: any) => a.order_nr - b.order_nr);

  const stagesMap: { [key: number]: Stage } = {};
  tempStagesArray.forEach((pdStage: any) => {
    stagesMap[pdStage.id] = {
      id: pdStage.id,
      title: pdStage.name,
      deals: [],
      dealCount: 0,
      totalValue: 0,
    };
  });

  rawDeals.forEach((deal: any) => {
    const stageId = deal.stage_id;
    if (stagesMap[stageId]) {
      const kanbanDeal: KanbanDeal = {
        id: deal.id,
        title: deal.title,
        stageId: deal.stage_id,
        stage: stagesMap[stageId]?.title || 'Unknown Stage',
        orgName: deal.organization?.name || deal.org_name,
        contactName: deal.person?.name || deal.person_name,
        nextActivitySubject: deal.next_activity_subject,
        notesCount: deal.notes_count,
        activityCount: deal.activities_count,
        ownerName: deal.user?.name || deal.owner_name,
        value: deal.value,
        currency: deal.currency,
        formatted_value: deal.formatted_value,
      };
      stagesMap[stageId].deals.push(kanbanDeal);
      stagesMap[stageId].dealCount += 1;
      stagesMap[stageId].totalValue += deal.value || 0;
    }
  });

  // Return the sorted stages
  return tempStagesArray
    .map((pdStage: any) => stagesMap[pdStage.id])
    .filter((stage) => stage !== undefined);
};

export default function KommuneDeals() {
  const [state, dispatch] = useReducer(reducer, initialState);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchAndProcessDeals = async () => {
      dispatch({ type: 'SET_LOADING', payload: true });
      setError(null);
      try {
        const response: any = await callPipedrive({
          requestType: 'GET_DEALS',
          start: 0,
          limit: 200,
          filterId: 58, // Your specific filter for deal title contains 'kommune'
        });

        const pipedriveResponse = response.data;

        if (pipedriveResponse.success === false) {
          console.error('Pipedrive API Error:', pipedriveResponse.error || pipedriveResponse);
          setError(
            pipedriveResponse.error_info ||
              pipedriveResponse.error ||
              'Failed to fetch deals from Pipedrive.'
          );
          dispatch({ type: 'SET_LOADING', payload: false });
          dispatch({
            type: 'SET_DATA',
            payload: { data: [], pagination: initialState.pagination, stages: [], pipelines: {} },
          });
          return;
        }

        const processedStages = processPipedriveResponse(pipedriveResponse);

        dispatch({
          type: 'SET_DATA',
          payload: {
            data: pipedriveResponse.data || [],
            pagination: pipedriveResponse.additional_data?.pagination || initialState.pagination,
            stages: processedStages,
            pipelines: pipedriveResponse.related_objects?.pipeline || {},
          },
        });
      } catch (e: any) {
        console.error('Error calling Pipedrive deals callable:', e);
        setError(e.message || 'An unexpected error occurred.');
        dispatch({
          type: 'SET_DATA',
          payload: { data: [], pagination: initialState.pagination, stages: [], pipelines: {} },
        });
      }
      dispatch({ type: 'SET_LOADING', payload: false });
    };

    fetchAndProcessDeals();
  }, []);

  if (state.loading) {
    return (
      <div
        style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}
      >
        <Spin size="large" />
      </div>
    );
  }

  if (error) {
    return <Alert message="Error" description={error} type="error" showIcon />;
  }

  if (!state.stages || state.stages.length === 0) {
    return (
      <div
        style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}
      >
        <Empty description="No deals found or stages could not be processed." />
      </div>
    );
  }

  return (
    <div>
      <KanbanBoard stages={state.stages} />
    </div>
  );
}
